import { Component, computed, OnInit, ViewChild } from '@angular/core';
import { ProviderOrganizationsService } from 'src/app/services/provider.organizations.service';
import { Router } from '@angular/router';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { entityExistsValidator } from 'src/app/validators/entity-exists.validator';
import { debounceTime, map, of, Subscription, switchMap, tap } from 'rxjs';
import { Role } from 'src/app/models/user/role.model';
import { UsersService } from 'src/app/services/users.service';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { DatePipe } from '@angular/common';
import { ColDef, GridApi, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { BtnCellRenderer } from 'src/app/modules/shared/components/btn-cell-renderer/btn-cell-renderer.component';
import { TextTooltipRenderer } from 'src/app/modules/shared/components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { baseColDef } from 'src/app/modules/shared/utils';
import { formatPhone } from 'src/app/modules/shared/utils/phone.util';
import { PhoneInputDirective } from 'src/app/modules/shared/directives/phone-input.directive';
import { tenDigitPhoneValidator } from 'src/app/modules/shared/validators/phone.validator';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FormStatusCellRenderer } from 'src/app/modules/shared/components/form-status/form-status-cell-renderer.component';
import { RightSidebarContainerComponent } from 'src/app/modules/shared/components/right-sidebar-container/right-sidebar-container.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SearchSortComponent } from 'src/app/modules/shared/components/search-sort/search-sort.component';
import { AgGridWrapperComponent } from 'src/app/modules/shared/components/ag-grid-wrapper/ag-grid-wrapper.component';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ISelect } from 'src/app/modules/shared/components/mat-multiselect/mat-multiselect.component';
import { BillingRuleService } from 'src/app/services/billingRule.service';
import { MatSelectModule } from '@angular/material/select';
import { StatesService } from 'src/app/services/states.service';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ConsultantsService } from 'src/app/services/consultants.service';  
@Component({
  selector: 'app-organization-list',
  templateUrl: './provider-organization-list.component.html',
  styleUrls: ['./provider-organization-list.component.scss'],
  standalone: true,
  imports: [
    MatSidenavModule,
    RightSidebarContainerComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    PhoneInputDirective,
    SearchSortComponent,
    AgGridWrapperComponent,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    MatCheckboxModule, 
  ]
})
export class ProviderOrganizationListComponent implements OnInit {
  isLoading: boolean;
  datePipe = new DatePipe('en-US');
  subscriptions: Subscription[] = [];
  loadingValidator: boolean;
  showAll: boolean = false;

  pageSettings: {
    defaultPageSize: number;
    pageSizeOptions: number[];
  } = {
    defaultPageSize: 20,
    pageSizeOptions: [20, 50, 100],
  };
  currentSortField = {
    sortValue: 'id',
  };
  currentIsAscending = true;
  sortForm: FormGroup = new FormGroup({
    sortField: new FormControl({
      sortValue: this.currentSortField.sortValue,
    }),
    isAscending: new FormControl(this.currentIsAscending),
  });

  sortOptions = [
    { label: 'Name', sortValue: 'name' },
  ];
  currentPage: number = 0;
  pageSize: number = this.pageSettings.defaultPageSize;
  searchControl: FormControl = new FormControl('');
  totalCount: number = 0;
  searchKey: string = '';
  billingRuleList: ISelect[] = [];
  statesList: ISelect[] = [];
  servicesOptions: ISelect[] = [
    { id: 1, name: 'Compliance Only' },
    { id: 2, name: 'Compliance + Forms Management' }
  ];

  public columnDefs: ColDef[] = [
  {
    ...baseColDef,
    field: 'name',
    cellRenderer: TextTooltipRenderer,
    cellRendererParams: {
      onClick: this.onView.bind(this)
    },
    cellStyle: (params): any => {
      if (params.data?.isChildRow) {
        return { paddingLeft: '40px' };
      }
      if (params.data?.isParentRow && params.data?.hasChildren) {
        return { fontWeight: 'bold' };
      }
      return null;
    },
    flex: 2
  }
];

  public rowData$!: any[];
  gridApi: GridApi;

  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isProvider = computed(() => this.userRole() === Role.Provider);
  readonly isSystem = computed(() => this.userRole() === Role.SystemUser);
  readonly isConsultant = computed(() => this.userRole() === Role.Consultant);
  readonly isProviderReadOnly = computed(() => this.userRole() === Role.ProviderReadOnly);
  readonly isLenderReadOnly = computed(() => this.userRole() === Role.LenderReadOnly);
  constructor(
    private service: ProviderOrganizationsService,
    private userService: UsersService,
    private deleteDialog: DeleteConfirmationDialogService,
    private billingRuleService: BillingRuleService,
    private statesService: StatesService,
    private router: Router,
    private consultantsService: ConsultantsService,
  ) {}

  @ViewChild('drawer') drawer: MatDrawer;
  isEditing = false;
  id: number;

  form: FormGroup = new FormGroup({
    name: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
    }),
    phoneNumber: new FormControl('', {
      validators: [
        Validators.required,
        tenDigitPhoneValidator(),
      ],
    }),
    mailingStreetAddress: new FormControl('', {
      validators: [Validators.required],
    }),
    mailingStreetAddressLine2: new FormControl(''),
    mailingCity: new FormControl('', { validators: [Validators.required] }),
    mailingStateId: new FormControl('', { validators: [Validators.required] }),
    mailingZipCode: new FormControl('', { validators: [Validators.required] }),
    billingRuleGroupId: new FormControl('', {
      validators: [Validators.required],
    }),
    providerType: new FormControl('', {
      validators: [Validators.required],
    }),
  });

  ngOnInit() {
    this.form.controls['name'].setAsyncValidators([
      entityExistsValidator(this.service, undefined, (isLoading: boolean) => {
        this.loadingValidator = isLoading;
      })
    ]);
    
    const searchSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          this.currentPage = 0;
          this.reload();
      }),
      )
      .subscribe();

    this.subscriptions.push(searchSubscription);
}
  openRightSidebar(data: any = null) {
    if (!this.billingRuleList.length) this._loadBillingRuleList();
    if (!this.statesList.length) this._loadStatesList();
    this.setValidators(data);
    if (data) {
      this.id = data.id;
      this.form.setValue({
        name: data.name,
        email: data.email || '',
        phoneNumber: data.phoneNumber || '',
        mailingStreetAddress: data.mailingStreetAddress || '',
        mailingStreetAddressLine2: data.mailingStreetAddressLine2 || '',
        mailingCity: data.mailingCity || '',
        mailingStateId: data.mailingStateId || '',
        mailingZipCode: data.mailingZipCode || '',
        billingRuleGroupId: data.billingRuleGroupId || '',
        providerType: data.providerType || '',
      });

      this.isEditing = true;
    } else {
      this.isEditing = false;
    }

    this.updateValueAndValidity();
    this.drawer.toggle();
  }

 onView(data: any) {
  if (this.isProvider() || this.isConsultant() || this.isProviderReadOnly()) {
    this.router.navigate([`/provider/organization/${data.id}`]);
  } else if (this.isLender() || this.isLenderReadOnly()) {
    this.router.navigate([`/lender/provider-organization-list/${data.id}`]);
  } else {
    this.router.navigate([`/admin/providers-list/${data.id}`]);
  }
}

  onConfirmation(result: any) {
    if (result.confirmed) {
      this.service.changeUserAvailability(result.id, false).subscribe(() => {
        this.reload();
      });
    }
  }

  close() {
    this.form.reset();
    this.drawer.close();
  }

  onSubmit() {
    if (this.form.valid) {
      if (this.isEditing) {
        this.isLoading = true;
        this.service
          .update(this.id.toString(), this.form.value)
          .subscribe((data) => {
            this.reload();
          });
      } else {
        this.isLoading = true;
        this.service.add(this.form.value).subscribe((data) => {
          this.reload();
        });
      }
    }
  }

  onChangeAvailability(data: any) {
    const hasChildren = data.hasChildren === true;
    const organizationType = hasChildren 
      ? 'provider organization and all its child organizations' 
      : 'provider organization';
  
    const actionMessage = data.isActive 
      ? `This ${organizationType} will be disabled.`
      : `This ${organizationType} will be enabled.`;
  
    this.deleteDialog
      .openDeleteConfirmation(
      actionMessage,
        data.isActive ? 'Disable confirmation' : 'Enable confirmation',
        data.isActive ? 'Disable' : 'Enable'
      )
      .afterClosed()
      .pipe(
        switchMap((dialogClosed) => {
          if (dialogClosed)
            return this.service.changeUserAvailability(data.id, !data.isActive);
          else return of(false);
        })
      )
      .subscribe((deleteConfirmation) => {
        if (deleteConfirmation) {
          this.reload();
        }
      });
  }
private transformDataToHierarchy(items: any[]): any[] {
  if (!items || items.length === 0) return [];
  
  const result: any[] = [];
  const processedIds = new Set<number>();
  
  const parentIds = new Set<number>();
  const childrenByParent = new Map<number, any[]>();
  
  items.forEach(item => {
    if (item.hasChildren && !item.parentOrganizationId) {
      parentIds.add(item.id);
    }
    if (item.parentOrganizationId) {
      if (!childrenByParent.has(item.parentOrganizationId)) {
        childrenByParent.set(item.parentOrganizationId, []);
      }
      childrenByParent.get(item.parentOrganizationId)!.push(item);
    }
  });
  
  items.forEach(item => {
    if (processedIds.has(item.id)) return;
    
    // Orphaned child (parent not on this page)
    if (item.parentOrganizationId && !parentIds.has(item.parentOrganizationId)) {
      result.push({
        ...item,
        isChildRow: !this.isConsultant(),
        isParentRow: false
      });
      processedIds.add(item.id);
      return;
    }
    
    // Child whose parent IS on this page - skip, will be added with parent
    if (item.parentOrganizationId) {
      return;
    }
    
    // Parent or standalone
    result.push({
      ...item,
      isParentRow: item.hasChildren || false,
      isChildRow: false
    });
    processedIds.add(item.id);
    
    // Add children immediately after parent
    if (childrenByParent.has(item.id)) {
      childrenByParent.get(item.id)!.forEach(child => {
        if (!processedIds.has(child.id)) {
          result.push({
            ...child,
            isChildRow: true,
            isParentRow: false
          });
          processedIds.add(child.id);
        }
      });
    }
  });
  
  return result;
}

private reload() {
  this.isLoading = true;
  this.searchKey = this.searchControl.value?.toLowerCase() || '';  
  if (this.gridApi) {
    this.gridApi.showLoadingOverlay();
  }
  if (this.isConsultant()) {
    this.loadConsultantAssignedOrganizations();
    return;
  }
  const params: any = {
    sortColumn: this.currentSortField.sortValue,
    sortOrder: this.currentIsAscending ? 'asc' : 'desc',
    page: this.currentPage + 1,
    pageSize: this.pageSize,
    searchKey: this.searchKey
  };
  if (!this.showAll) {
    params.isActive = true;
  }

  const subscription = this.service
    .get(params)
    .pipe(
      switchMap((response: any) => {
        if (!response.isError) {
          return of(response.data);
        } else {
          return of([]);
        }
      })
    )
    .subscribe((data) => {
      if (this.gridApi) {
        this.gridApi.hideOverlay();
      }
      this.rowData$ = this.transformDataToHierarchy(data.items);
      this.totalCount = data.totalCount;
      this.isLoading = false;
      this.form.reset();
      this.drawer.close();
    }); 
    this.subscriptions.push(subscription);
  }

private loadConsultantAssignedOrganizations() {
  const params: any = {
    sortColumn: this.currentSortField.sortValue,
    sortOrder: this.currentIsAscending ? 'asc' : 'desc',
    page: this.currentPage + 1,
    pageSize: this.pageSize,
    searchKey: this.searchKey
  };

  const subscription = this.consultantsService
    .getCurrentConsultantAssignedOrganizations(params)  
    .pipe(
      switchMap((response: any) => {
        if (!response.isError) {
          return of(response.data);
        } else {
          return of([]);
        }
      })
    )
    .subscribe((data) => {
      if (this.gridApi) {
        this.gridApi.hideOverlay();
      }
      this.rowData$ = this.transformDataToHierarchy(data.items); 
      this.totalCount = data.totalCount;
      this.isLoading = false;
      this.form.reset();
      this.drawer.close();
    });
  this.subscriptions.push(subscription);
}

  onShowAllChange(): void {
    this.currentPage = 0; 
    this.reload();
  }
  handlePageEvent(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload();
  }

  setSortField() {
    const { sortField, isAscending } = this.sortForm.value;
    this.currentIsAscending = isAscending;
    this.currentSortField = sortField;

    this.reload();
  }

  resetSortField() {
    this.sortForm.setValue({
      sortField: this.currentSortField,
      isAscending: this.currentIsAscending
    })
  }
public getRowStyle = (params: any) => {
  if (params.data?.isParentRow) {
    return { background: '#f5f8fa' };  
  }
  return { background: '#ffffff' }; 
};
  //------------GRID FUNCTIONS
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.reload();
    this.adjustColumnVisibility(this.gridApi);
  }

  private adjustColumnVisibility(gridApi: any) {
    const columns = [...this.columnDefs];
    
    // Only show Services column for non-Lenders
    if (!this.isLender() && !this.isLenderReadOnly()) {
      columns.push(
        {
          ...baseColDef,
          field: 'providerType',
          headerName: 'Services',
          cellRenderer: TextTooltipRenderer,
          valueGetter: (params: ValueGetterParams) => {
            const providerType = params.data?.providerType;
            if (providerType === 1) return 'Compliance Only';
            if (providerType === 2) return 'Compliance + Forms Management';
            return 'Not Set';
          },
          flex: 2
        }
      );
    }
    
    columns.push(
        {
          ...baseColDef,
          field: 'email',
          headerName: 'Email',
          cellRenderer: TextTooltipRenderer,
          flex: 2 
        },
        {
          ...baseColDef,
          field: 'phoneNumber',
          headerName: 'Phone Number',
          cellRenderer: TextTooltipRenderer,
          valueFormatter: (params) => formatPhone(params.value),
          flex: 2 
        },
        {
          ...baseColDef,
          field: 'physicalAddress',
          headerName: 'Address',
          cellRenderer: TextTooltipRenderer,
          valueGetter: (params: ValueGetterParams) => {
            const data = params.data;
            if (!data) return '';

            const parts = [];
            if (data.mailingStreetAddress)
              parts.push(data.mailingStreetAddress);
            if (data.mailingStreetAddressLine2)
              parts.push(data.mailingStreetAddressLine2);
            if (data.mailingCity) parts.push(data.mailingCity);
            if (data.mailingStateName) parts.push(data.mailingStateName);
            if (data.mailingZipCode) parts.push(data.mailingZipCode);

            return parts.join(', ');
          },
          flex: 3,
        },
       {
      ...baseColDef,
      field: 'isActive',
      valueGetter: (params) => {
        return params?.data?.isActive ? {
              statusId: 5,
          name: 'Enabled'
        }: {
              statusId: 6,
          name: 'Disabled'
        }
      },
      headerName: 'Status',
      width: 150,
      cellRenderer: FormStatusCellRenderer,
      comparator: (a, b) => (a?.statusId ?? 0) - (b?.statusId ?? 0),
    },
      );

    // Add action column for non-lenders
    if (this.isAdmin() || this.isSystem()) {
      const actionCol = {
        field: 'action',
        minWidth: 80,
        maxWidth: 80,
        filter: false,
        sortable: false,
        cellRenderer: BtnCellRenderer,
        cellRendererParams: {
          onEdit: this.openRightSidebar.bind(this),
          onChangeAvailability: this.isAdmin() ? this.onChangeAvailability.bind(this) : undefined,
        },
      };
      columns.push(actionCol);
    }

    gridApi.setColumnDefs(columns);
  }

  private _loadBillingRuleList(): void {
    this.billingRuleService.get({
        pageSize: 100,
        sortOrder: 'asc',
      })
    .pipe(
      map((res: any) => res.isError ? [] : res.data)
    )
    .subscribe(items => {
        this.billingRuleList = items;
      });
  }

  private _loadStatesList(): void {
    this.statesService
      .get({
        pageSize: 100,
        sortOrder: 'asc',
      })
      .pipe(map((res: any) => (res.isError ? [] : res.data)))
      .subscribe((items) => {
        this.statesList = items;
      });
  }

  private updateValueAndValidity() {
    this.form.controls['name'].updateValueAndValidity();
  }

  private setValidators(data: any = null) {
    this.form.controls['name'].setAsyncValidators([
      entityExistsValidator(this.service, data?.name),
    ]);
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(s => s.unsubscribe());
  }
}